/* MASONTEX · React Media Island (MVP)
   Replaces only the product gallery interaction inside
   sections/ms-v12-product-overview.liquid.
   Liquid SSR + Media Core (window.MasontexMediaCore) stay owners of data,
   resolution, preload and cache. React owns interaction state + rendering.
   Contracts preserved: oem-lite:ready / oem-lite:change, dataset flags,
   identical CSS classes (no CSS rewrite). */
(function () {
  'use strict';

  const React = window.React;
  const ReactDOM = window.ReactDOM;
  if (!React || !ReactDOM) {
    console.error('[MASONTEX Media React] missing React or ReactDOM');
    return;
  }

  const { useState, useEffect, useMemo, useRef } = React;
  const h = React.createElement;
  const getMountRegistry = () => {
    if (!window.__MASONTEX_MEDIA_REACT_MOUNTS) {
      window.__MASONTEX_MEDIA_REACT_MOUNTS = new WeakSet();
    }
    return window.__MASONTEX_MEDIA_REACT_MOUNTS;
  };
  const coreReady = () => {
    const core = window.MasontexMediaCore;
    return !!(core && core.normalize && core.capabilities && core.resolver && core.preload && core.cache && core.delivery);
  };

  const readJson = (root, selector, fallback) => {
    const scope = root.closest('section') || root;
    const node = scope.querySelector(selector);
    return window.MasontexMediaCore.normalize.parseJson(node && node.textContent, fallback);
  };

  class ErrorBoundary extends React.Component {
    constructor(props) {
      super(props);
      this.state = { failed: false };
    }
    static getDerivedStateFromError() {
      return { failed: true };
    }
    componentDidCatch(error) {
      try {
        const fallback = this.props.root.querySelector('[data-ssr-hero]');
        if (fallback) fallback.removeAttribute('hidden');
        this.props.root.dataset.mediaUnavailable = 'true';
      } catch (e) { /* ignore */ }
      console.error('[MASONTEX Media React] island error', error);
    }
    render() {
      return this.state.failed ? null : this.props.children;
    }
  }

  function MediaIsland({ root }) {
    const { normalize, capabilities, resolver, preload, cache, delivery } = window.MasontexMediaCore;
    const scope = useMemo(() => root.closest('section') || root, [root]);
    const config = useMemo(() => readJson(root, '[data-lite-config]', {}), [root, scope]);
    const productMedia = useMemo(
      () => readJson(root, '[data-lite-media]', []).filter((item) => item && item.image),
      [root, scope]
    );
    const native = useMemo(() => readJson(root, '[data-ms-product-page-i18n]', {}), [root, scope]);
    const displayValues = native.values || {};
    const overviewText = native.overview || {};
    const mediaText = native.media || {};

    const hasFullConfig = normalize.hasFullConfig(config);
    const lists = useMemo(
      () => normalize.buildLists({ config, productMedia, dataset: root.dataset, hasFullConfig }),
      [config, productMedia, hasFullConfig, root]
    );
    const colorOptionGroups = useMemo(
      () => normalize.colorOptionGroups(config, lists.colors),
      [config, lists.colors]
    );
    const defaults = config.defaultState || {};
    const initial = useMemo(() => normalize.initialState(lists, defaults), [lists, defaults]);

    const [color, setColor] = useState(initial.color);
    const [visor, setVisor] = useState(initial.visor);
    const logo = initial.logo || '';
    const [structure, setStructure] = useState(initial.structure);
    const [view, setView] = useState(initial.view);
    const [activeTab, setActiveTab] = useState(
      root.dataset.showColor !== 'false' && lists.colors.length ? 'colors' : 'visors'
    );
    // Track the exact decoded URL; a previous image must not clear the current loading state.
    const [readyBase, setReadyBase] = useState('');
    const [overlayReady, setOverlayReady] = useState('');
    const [controlsOpen, setControlsOpen] = useState(false);

    const controlsToggle = useRef(null);
    useEffect(() => {
      const media = window.matchMedia('(max-width:749px)');
      const update = () => setControlsOpen(false);
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    }, [root]);

    const labels = {
      color: root.dataset.labelColor || 'COLOR',
      visor: root.dataset.labelVisor || 'VISOR',
      view: root.dataset.labelView || 'VIEW',
      structure: root.dataset.labelStructure || 'STRUCTURE'
    };

    const state = { color, visor, structure, view };
    const selectedValues = { ...state, logo };
    const ctx = { config, productMedia, lists, hasFullConfig, state };
    const mediaCache = useRef(cache.createMediaCache(96));
    const warmCache = useRef(preload.createWarmCache());
    const mountedRef = useRef(false);

    const payloadFor = (c, v, s, w) => {
      const key = cache.payloadKey(c, v, s, w);
      const hit = mediaCache.current.get(key);
      if (hit !== undefined) return hit;
      const payload = resolver.payloadFor(ctx, c, v, s, w);
      if (payload) mediaCache.current.set(key, payload);
      return payload;
    };

    const commitMediaState = (next) => {
      if (!next) return;
      if (next.color !== color) setColor(next.color);
      if (next.visor !== visor) setVisor(next.visor);
      if (next.structure !== structure) setStructure(next.structure);
      if (next.view !== view) setView(next.view);
    };

    const transitionMediaState = (type, value) => {
      commitMediaState(resolver.transitionState(ctx, { type, value }));
    };

    // Normalize only the initial state. User interaction is handled atomically by
    // transitionMediaState so intermediate invalid combinations never render.
    useEffect(() => {
      commitMediaState(resolver.ensureCompatible(ctx));
      mountedRef.current = true;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Normal interaction renders exact media only. Recovery fallbacks are reserved
    // for actual load failures and never silently change the visual semantics.
    const selectedPayload = useMemo(() => {
      const options = { width: delivery.widthForViewport(window.innerWidth) };
      const payload = payloadFor(color, visor, structure, view);
      if (!payload) return null;
      return delivery.transformPayload(payload, options);
    }, [color, visor, structure, view, hasFullConfig]);

    const ssrHero = root.querySelector('[data-ssr-hero]');
    const activePayload = selectedPayload ? { ...selectedPayload } : null;
    if (activePayload && ssrHero && normalize.sameAsset(activePayload.base, ssrHero.currentSrc || ssrHero.src)) {
      activePayload.base = ssrHero.currentSrc || ssrHero.src;
    }
    const activeBase = activePayload ? activePayload.base : '';
    const loading = Boolean(activeBase) && readyBase !== activeBase;
    const [shownBase, setShownBase] = useState(activeBase);
    const latestBase = useRef(activeBase);
    latestBase.current = activeBase;
    const shownSelection = useRef({ color, visor, structure, view });
    useEffect(() => {
      if (shownBase === activeBase && activePayload && (!activePayload.overlay || overlayReady === activePayload.overlay)) {
        shownSelection.current = { color, visor, structure, view };
      }
    }, [shownBase, activeBase, color, visor, structure, view, overlayReady]);

    // mediaUnavailable dataset contract
    useEffect(() => {
      const fallback = root.querySelector('[data-ssr-hero]');
      if (activePayload) {
        delete root.dataset.mediaUnavailable;
      } else {
        root.dataset.mediaUnavailable = '1';
        if (fallback) fallback.removeAttribute('hidden');
      }
    }, [activePayload, root]);

    // adaptive prewarm (debounced, same URL coverage)
    const warmForCurrentState = () => {
      const options = { width: delivery.widthForViewport(window.innerWidth) };
      const prefetchPayload = (c, v, s, w) => {
        const payload = delivery.transformPayload(payloadFor(c, v, s, w), options);
        if (payload && ssrHero && normalize.sameAsset(payload.base, ssrHero.currentSrc || ssrHero.src)) {
          payload.base = ssrHero.currentSrc || ssrHero.src;
        }
        return payload;
      };
      preload.prefetch(ctx, warmCache.current, prefetchPayload);
    };
    const warmStateRef = useRef(warmForCurrentState);
    warmStateRef.current = warmForCurrentState;
    const warmScheduler = useRef(preload.createDebounced(() => warmStateRef.current(), 500));
    useEffect(() => () => warmScheduler.current.cancel(), []);

    const rawMoq = Number(root.dataset.moq);
    const confirmedMoq = Number.isFinite(rawMoq) && rawMoq > 0 ? rawMoq : null;

    const displayValue = (raw) => normalize.translate(raw, displayValues);
    const selectedLabel = (type) =>
      normalize.itemLabel(
        normalize.findItem(lists[`${type}s`] || [], selectedValues[type]),
        normalize.titleCase(selectedValues[type])
      );

    const configurationCode = () => [
      root.dataset.model || 'MODEL',
      normalize.findItem(lists.colors, color)?.code || normalize.shortCode(color, 'COL'),
      lists.visors.length ? (normalize.findItem(lists.visors, visor)?.code || normalize.shortCode(visor, 'VIS')) : 'NOV',
      normalize.findItem(lists.logos, logo)?.code || normalize.shortCode(logo, 'LOG'),
      confirmedMoq ?? 'UNCONFIRMED'
    ].join('-');

    const configuration = () => ({
      product: root.dataset.productTitle,
      productUrl: root.dataset.productUrl,
      model: root.dataset.model,
      color: selectedLabel('color'),
      visor: lists.visors.length ? selectedLabel('visor') : 'Not applicable',
      logo: lists.logos.length ? selectedLabel('logo') : 'Not applicable',
      structure: lists.structures.length ? selectedLabel('structure') : 'Not applicable',
      view: selectedLabel('view'),
      customOptions: [],
      notes: '',
      moq: confirmedMoq,
      certification: root.dataset.certification,
      code: configurationCode()
    });

    // summary / field sync into the SSR info column + change event
    useEffect(() => {
      const scope = root.closest('section') || root;
      const na = displayValues['N/A'] || 'N/A';
      const values = {
        model: root.dataset.model,
        color: displayValue(selectedLabel('color')),
        visor: lists.visors.length ? displayValue(selectedLabel('visor')) : na,
        logo: lists.logos.length ? displayValue(selectedLabel('logo')) : na,
        moq: confirmedMoq ?? ''
      };
      Object.entries(values).forEach(([name, value]) => {
        scope.querySelectorAll(`[data-lite-summary="${name}"]`).forEach((node) => { node.textContent = value; });
      });
      const configCode = scope.querySelector('[data-lite-code]');
      if (configCode) configCode.textContent = configurationCode();
      const quantity = scope.querySelector('[data-lite-quantity]');
      if (quantity) {
        const minimum = confirmedMoq ?? 1;
        quantity.min = String(minimum);
        if (Number(quantity.value) < minimum) quantity.value = String(minimum);
      }
      const details = configuration();
      scope.querySelectorAll('[data-lite-field]').forEach((input) => {
        const field = input.dataset.liteField;
        input.value = field === 'json'
          ? JSON.stringify(details)
          : Array.isArray(details[field])
            ? details[field].join(', ')
            : String(details[field] ?? '');
      });
      if (mountedRef.current) {
        root.dispatchEvent(new CustomEvent('oem-lite:change', { bubbles: true, detail: details }));
      }
    }, [color, visor, structure, view]);

    // ready event on mount (after first summary sync)
    useEffect(() => {
      root.dispatchEvent(new CustomEvent('oem-lite:ready', { bubbles: true, detail: configuration() }));
      root.dataset.oemLiteReady = '1';
      root.dataset.oemLiteVersion = '12.2-native-display';
      // The selected SSR hero must win the first network budget. Nearby views
      // are opportunistic and must not start competing before window load.
      let firstWarmTimer = null;
      const scheduleInitialWarm = () => {
        firstWarmTimer = window.setTimeout(() => {
          firstWarmTimer = null;
          if (root.isConnected) warmScheduler.current.schedule();
        }, 3500);
      };
      if (document.readyState === 'complete') scheduleInitialWarm();
      else window.addEventListener('load', scheduleInitialWarm, { once: true });
      return () => {
        window.removeEventListener('load', scheduleInitialWarm);
        if (firstWarmTimer !== null) window.clearTimeout(firstWarmTimer);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // adaptive prewarm after meaningful state change
    useEffect(() => {
      if (mountedRef.current) warmScheduler.current.schedule();
    }, [color, visor, structure, view]);

    const showColor = root.dataset.showColor !== 'false';
    const showVisor = root.dataset.showVisor !== 'false' && lists.visors.length > 0;
    const showViews = root.dataset.showViews !== 'false';
    const showStructure = root.dataset.showStructure !== 'false';
    const tabs = [];
    if (showColor) tabs.push({ key: 'colors', label: labels.color });
    if (showVisor) tabs.push({ key: 'visors', label: labels.visor });

    const tabNav = (event) => {
      const keys = tabs.map((t) => t.key);
      const index = keys.indexOf(activeTab);
      let next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % keys.length;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + keys.length) % keys.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = keys.length - 1;
      if (next !== null) {
        event.preventDefault();
        setActiveTab(keys[next]);
      }
    };

    const choiceTone = (item, index) => item.tone
      || (Array.isArray(item.swatches) && item.swatches.length ? `linear-gradient(135deg,${item.swatches.join(',')})` : '')
      || item.swatch
      || item.hex
      || item.color
      || ['#f4f5f6', '#171b20', '#ffffff', '#7d8791'][index % 4];

    const renderChoiceList = (name, type) => {
      const items = lists[name] || [];
      const select = (key) => transitionMediaState(type, key);
      return h('div', { className: 'ms-lite-choice-grid' },
        items.map((item, index) => {
          const key = normalize.itemKey(item);
          const active = state[type] === key;
          const children = [];
          children.push(h('i', { key: 'sw', 'aria-hidden': true, style: { background: item.swatches?.[0] || item.hex || choiceTone(item, index) } }));
          const label = displayValue(normalize.itemLabel(item, normalize.titleCase(key)));
          children.push(h('strong', { key: 'label' }, label));
          if (item.description) {
            const translated = displayValue(item.description);
            if (translated !== item.description || !/[A-Za-z]{3,}/.test(item.description)) {
              children.push(h('small', { key: 'desc' }, translated));
            }
          }
          return h('button', {
            key,
            type: 'button',
             className: 'ms-lite-choice' + (active ? ' is-active' : ''),
             'aria-pressed': active,
             'data-type': type,
            'data-key': key,
             'aria-label': label,
            onClick: () => select(key)
          }, children);
        })
      );
    };

    const renderGroupedColorControls = () => colorOptionGroups.map((group) => {
      const activeOption = normalize.activeColorGroupOption(group, color);
      return h('div', { key: group.key, className: 'ms-lite-control-row ms-media-color-group' },
        h('div', { className: 'ms-v12-option-label' }, displayValue(group.label)),
        h('div', { className: 'ms-lite-segments' }, group.options.map((option) => {
          const targetColor = normalize.resolveGroupedColor(colorOptionGroups, color, group.key, option.key);
          const active = activeOption?.key === option.key;
          const available = !!targetColor;
          return h('button', {
            key: option.key,
            type: 'button',
            className: 'ms-lite-segment' + (active ? ' is-active' : '') + (!available ? ' is-unavailable' : ''),
            'data-type': `color-group-${group.key}`,
            'data-key': option.key,
            'aria-pressed': active,
            'aria-disabled': !available,
            disabled: !available,
            onClick: () => available && transitionMediaState('color', targetColor)
          },
            option.tone
              ? h('i', { className: 'ms-media-color-swatch', 'aria-hidden': true, style: { background: option.tone } })
              : null,
            displayValue(option.label)
          );
        }))
      );
    });

    const renderSegments = (items, type) => {
      const select = (key) => transitionMediaState(type, key);
      return items.map((item, index) => {
        const key = normalize.itemKey(item);
        const available = type === 'structure'
          ? resolver.supportedStructure(ctx, key)
          : resolver.supportedView(ctx, structure, key);
        const active = state[type] === key;
        const label = type === 'view'
          ? normalize.viewLabelWithI18n({ ...item, label: normalize.itemLabel(item) }, index, displayValues, overviewText)
          : displayValue(normalize.itemLabel(item, normalize.titleCase(key)));
        return h('button', {
          key,
          type: 'button',
          className: 'ms-lite-segment'
            + (active ? ' is-active' : '')
            + (!available ? ' is-unavailable' : ''),
          'aria-pressed': active,
          'aria-disabled': !available,
          disabled: !available,
          'data-type': type,
          'data-key': key,
          onClick: () => select(key)
        }, label);
      });
    };

    const viewRowHidden = !showViews || lists.views.length < 2;
    const structureRowHidden = !showStructure || lists.structures.length < 2;
    const activePayloadKey = activeBase || 'none';

    const optionTabs = tabs.length > 1 ? h('div', { className: 'ms-v12-option-tabs', role: 'tablist', onKeyDown: tabNav },
        tabs.map((tab) => h('button', {
          key: tab.key,
          type: 'button',
          className: 'ms-v12-tab' + (activeTab === tab.key ? ' is-active' : ''),
          role: 'tab',
          'aria-selected': activeTab === tab.key ? 'true' : 'false',
          onClick: () => setActiveTab(tab.key)
        }, tab.label))
      ) : null;
    const optionPanels = h('div', { className: 'ms-v12-option-panels' },
        showColor ? h('div', {
          className: 'ms-v12-option-panel' + (activeTab === 'colors' ? ' is-active' : ''),
          role: 'tabpanel',
          hidden: activeTab !== 'colors'
        }, tabs.length > 1 ? null : h('p', { className: 'ms-v12-option-label' }, labels.color), renderChoiceList('colors', 'color')) : null,
        showVisor ? h('div', {
          className: 'ms-v12-option-panel' + (activeTab === 'visors' ? ' is-active' : ''),
          role: 'tabpanel',
          hidden: activeTab !== 'visors'
        }, tabs.length > 1 ? null : h('p', { className: 'ms-v12-option-label' }, labels.visor), renderChoiceList('visors', 'visor')) : null
      );
    const viewControls = showViews ? h('div', { className: 'ms-v12-view-rail ms-lite-control-row', hidden: viewRowHidden },
          h('div', { className: 'ms-v12-view-title' }, labels.view),
          h('div', { className: 'ms-lite-segments' }, renderSegments(lists.views, 'view'))
        ) : null;
    const imageStage = h('div', { className: 'ms-v12-stage' },
          h('div', { className: 'ms-lite-image-area' },
            h('div', { className: 'ms-lite-shadow', 'aria-hidden': true }),
            activePayload
              ? h('img', {
                  key: activePayloadKey,
                  className: 'ms-lite-image is-active',
                  'data-lite-image-a': '',
                  src: activeBase,
                  decoding: 'async',
                  fetchPriority: 'high',
                  style: shownBase !== activeBase ? { opacity: 0 } : undefined,
                  alt: root.dataset.productTitle || '',
                  onLoad: (event) => {
                    const image = event.currentTarget;
                    const reveal = () => {
                      if (!image.isConnected || latestBase.current !== activeBase) return;
                      setShownBase(activeBase);
                      setReadyBase(activeBase);
                      delete root.dataset.mediaLoadError;
                      root.querySelector('[data-ssr-hero]')?.setAttribute('hidden', '');
                    };
                    if (typeof image.decode === 'function') image.decode().then(reveal, reveal);
                    else reveal();
                  },
                  onError: () => {
                    setReadyBase('');
                    root.dataset.mediaLoadError = '1';
                    commitMediaState(shownSelection.current);
                  }
                })
              : null,
            shownBase && shownBase !== activeBase
              ? h('img', {
                  key: 'previous-' + shownBase,
                  className: 'ms-lite-image is-active',
                  src: shownBase,
                  alt: '',
                  'aria-hidden': true
                })
              : null,
            activePayload && activePayload.overlay
              ? h('img', {
                  key: 'overlay-' + activePayload.overlay,
                  className: 'ms-lite-overlay' + (overlayReady === activePayload.overlay && shownBase === activeBase ? ' is-visible' : ''),
                  'data-lite-overlay': '',
                  src: activePayload.overlay,
                  decoding: 'async',
                  style: {
                    filter: activePayload.filter || '',
                    visibility: overlayReady === activePayload.overlay && shownBase === activeBase ? 'visible' : 'hidden'
                  },
                  onLoad: (event) => {
                    if (event.currentTarget.isConnected) setOverlayReady(activePayload.overlay);
                  },
                  onError: () => setOverlayReady(''),
                  alt: ''
                })
              : null,
            h('div', { className: 'ms-lite-loading' + (loading ? '' : ' is-hidden'), 'data-lite-loading': '' })
          )
        );
    const structureControls = showStructure ? h('div', {
        className: 'ms-v12-structure ms-lite-control-row',
        hidden: structureRowHidden
      },
        h('div', { className: 'ms-v12-structure-label' }, labels.structure),
        h('div', { className: 'ms-lite-segments' }, renderSegments(lists.structures, 'structure'))
      ) : null;
    const panelId = (scope.id || 'product') + '-media-controls';
    const colorControls = showColor && colorOptionGroups.length
      ? renderGroupedColorControls()
      : null;
    const closeControls = () => { setControlsOpen(false); controlsToggle.current?.focus(); };

    return h('div', {
      className: 'ms-media-workspace' + (controlsOpen ? ' is-open' : ''),
      onKeyDown: (event) => { if (event.key === 'Escape' && controlsOpen) { event.preventDefault(); closeControls(); } }
    },
      h('button', {
        type: 'button',
        className: 'ms-media-toggle ms-cta-motion' + (controlsOpen ? ' is-open' : ''),
        ref: controlsToggle,
        'aria-label': controlsOpen ? (mediaText.close_controls || 'Close product display controls') : (mediaText.open_controls || 'Open product display controls'),
        'aria-expanded': controlsOpen,
        'aria-controls': panelId,
        onClick: () => setControlsOpen(!controlsOpen)
      },
        h('span', { className: 'ms-cta-motion__circle', 'aria-hidden': true }),
        h('span', { className: 'ms-media-toggle__icon', 'aria-hidden': true },
          controlsOpen
            ? h('span', { className: 'ms-media-toggle__close' }, '×')
            : h('svg', { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6 },
                h('path', { d: 'M4 7h16M4 17h16' }),
                h('circle', { cx: 9, cy: 7, r: 3 }),
                h('circle', { cx: 15, cy: 17, r: 3 }))),
        h('span', { className: 'ms-cta-motion__text' }, controlsOpen ? (mediaText.close || 'CLOSE') : (mediaText.adjust || 'ADJUST'))
      ),
      h('div', {
        id: panelId,
        className: 'ms-media-panel' + (controlsOpen ? ' is-open' : ''),
        role: 'region',
        'aria-label': mediaText.controls_label || 'Product display controls',
        'aria-hidden': controlsOpen ? 'false' : 'true'
      }, colorControls || [optionTabs, optionPanels], viewControls, structureControls),
      h('div', { className: 'ms-v12-stage-wrap' }, imageStage)
    );
  }

  const pendingMounts = new Set();
  let coreWaitTimeout = null;
  const rootsIn = (scope = document) => {
    const roots = [...scope.querySelectorAll('[data-react-media-root]')];
    if (scope.matches?.('[data-react-media-root]')) roots.unshift(scope);
    return roots;
  };

  function clearCoreWait() {
    clearInterval(window.__msReactCoreWait);
    window.__msReactCoreWait = null;
    clearTimeout(coreWaitTimeout);
    coreWaitTimeout = null;
  }

  function mount(root) {
    const mounts = getMountRegistry();
    if (!root.isConnected || mounts.has(root)) return;
    const mountPoint = root.querySelector('[data-react-mount-point]') || root;
    const reactRoot = ReactDOM.createRoot(mountPoint);
    mounts.add(root);
    root.__msReactRoot = reactRoot;
    reactRoot.render(h(ErrorBoundary, { root }, h(MediaIsland, { root })));
  }

  function unmount(scope) {
    rootsIn(scope).forEach(root => {
      pendingMounts.delete(root);
      root.__msReactRoot?.unmount();
      delete root.__msReactRoot;
      getMountRegistry().delete(root);
      for (const flag of ['oemLiteReady', 'oemLiteVersion', 'mediaUnavailable', 'mediaLoadError']) delete root.dataset[flag];
      root.querySelector('[data-ssr-hero]')?.removeAttribute('hidden');
    });
    if (!pendingMounts.size) clearCoreWait();
  }

  function boot(scope = document) {
    const roots = rootsIn(scope).filter(root => root.isConnected);
    if (coreReady()) {
      roots.forEach(mount);
      return;
    }
    roots.forEach(root => pendingMounts.add(root));
    if (!pendingMounts.size || window.__msReactCoreWait) return;
    window.__msReactCoreWait = setInterval(() => {
      if (!coreReady()) return;
      clearCoreWait();
      pendingMounts.forEach(mount);
      pendingMounts.clear();
    }, 100);
    coreWaitTimeout = setTimeout(() => {
      clearCoreWait();
      if (!coreReady()) {
        console.error('[MASONTEX Media React] MasontexMediaCore did not load within 5s');
        pendingMounts.forEach(root => { if (root.isConnected) root.dataset.mediaUnavailable = 'true'; });
      }
      pendingMounts.clear();
    }, 5000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
})();
